/**
 * The transient notification (redesign §34), kept for one case: an export the
 * server prepares (« Export started — runs-2026-10-18.csv »). Everything else
 * says its result where it happened. Ink #1D1D1F, white 13.5, radius 12, the
 * menu shadow, one line and a ×, bottom left of the content column (the
 * screen's bottom on a phone), layer 45, `role="status"`. It stays 4 s, paused
 * while the pointer or the focus is on it; a new one replaces the old. Enters
 * o + y 12→0 (d 1 r .35), leaves in 120 ms. Never for an error.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { createPortal } from 'react-dom';
import { shadow } from '../theme/tokens';
import { useMotionMount } from '../lib/presence';
import { CloseMark } from './icons';

type Listener = (text: string) => void;
const listeners = new Set<Listener>();

/** Shows `text` in the one notification, replacing any shown. */
export function notify(text: string): void {
  for (const listener of listeners) listener(text);
}

/** Mounted once beside the app; draws the notification while there is one. */
export function Toaster(): React.JSX.Element | null {
  const [shown, setShown] = useState<{ text: string; key: number } | null>(null);
  useEffect(() => {
    const listener: Listener = (text) => setShown({ text, key: Date.now() });
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  if (!shown) return null;
  return createPortal(<Toast key={shown.key} text={shown.text} onGone={() => setShown(null)} />, document.body);
}

function Toast({ text, onGone }: { text: string; onGone: () => void }): React.JSX.Element {
  const motion = useMotionMount<HTMLDivElement>({ o: 0, y: 12 }, { d: 1, r: 0.35 }, { o: 0 });
  const [held, setHeld] = useState(false);
  const left = useRef(4000);
  useEffect(() => {
    if (held) return;
    const started = performance.now();
    const timer = window.setTimeout(onGone, left.current);
    return () => {
      window.clearTimeout(timer);
      left.current = Math.max(0, left.current - (performance.now() - started));
    };
  }, [held, onGone]);
  return (
    <div
      ref={motion}
      role="status"
      className="al-toast"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
      style={{
        position: 'fixed',
        bottom: 24,
        zIndex: 45,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        maxWidth: 'calc(100vw - 32px)',
        padding: '10px 10px 10px 16px',
        borderRadius: 12,
        background: '#1D1D1F',
        color: '#FFFFFF',
        fontSize: 13.5,
        boxShadow: shadow.menu
      }}
    >
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onGone}
        style={{
          display: 'flex',
          padding: 4,
          border: 0,
          borderRadius: 6,
          background: 'transparent',
          color: 'rgba(255,255,255,.72)',
          cursor: 'pointer'
        }}
      >
        <CloseMark />
      </button>
    </div>
  );
}
